import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Box, theme } from '@factorialco/deprecated-design-system'
import { BaseTabs, Spinner } from '@factorialco/f0-react/dist/experimental'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateEmailModuleRequest } from 'modules/marketplace_modules/hooks/useCreateEmailModuleRequest'
import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import useBookMeeting from 'modules/upsell/hooks/useBookMeeting'
import {
  ModalFooter,
  ModalHeader,
  OverviewTab,
  PlansTab,
  ScrollbarStyles,
} from 'modules/upselling_kit/components/ProductModalExtended/_Content'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import {
  buildUpsellOriginParam,
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

interface ProductModalExtendedProps extends UpsellingKitModule {
  isOpen: boolean
  onClose: () => void
  pageSource: string
  openFromUpsellButton?: boolean
  onRequest?: () => void
  children?: React.ReactNode
  upsellRequestComponentType?: string
  /** When provided, replaces "What's included" list from the query (same as ProductModal benefits prop). */
  benefits?: string[]
}

const ProductModalExtendedContent = ({
  moduleRequested,
  requestSource,
  isOpen,
  onClose,
  openFromUpsellButton = true,
  onRequest,
  children: _children,
  upsellRequestComponentType,
  benefits: benefitsProp,
}: ProductModalExtendedProps) => {
  const navigate = useNavigate()
  const { getDetailPath } = useMarketplaceModulesPath()
  const { create: createEmailRequest } = useCreateEmailModuleRequest()
  const [activeTabId, setActiveTabId] = useState<string>('overview')
  const {
    loading,
    moduleCarouselVideo,
    moduleCarouselImages,
    moduleSlug,
    upsellModalBenefits,
    moduleValues,
    moduleName,
    moduleDescription,
    moduleAvatar,
    billingAddonReference,
    navatticImageUrl,
  } = useGetUpsellingData({ moduleRequested })

  const {
    meetingLink,
    loading: meetingLoading,
    error: meetingError,
  } = useBookMeeting({
    moduleRequested,
  })
  const hasMeetingLink = !meetingLoading && !meetingError && !!meetingLink

  const validFeatures =
    moduleValues?.filter(
      (feature): feature is { emoji: string; title: string; description: string } =>
        Boolean(feature.emoji && feature.title && feature.description)
    ) ?? []

  const whatsIncludedBenefits = benefitsProp ?? upsellModalBenefits
  const hasPlansContent = billingAddonReference.length > 0

  useEffect(() => {
    if (isOpen && !openFromUpsellButton) {
      handleUpsellingVisibility({
        moduleRequested,
        requestSource,
        component: 'product_modal',
      })
    } else if (isOpen && openFromUpsellButton) {
      track(trackingEvents.upsellingKit.openProductModal, {
        source: requestSource,
        module: moduleRequested,
        component: 'product_modal',
      })
    }
  }, [isOpen, moduleRequested, requestSource, openFromUpsellButton])

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_modal',
    })
  }

  const getDetailPathWithParams = (action?: string) => {
    const componentName = upsellRequestComponentType ?? 'product_modal'
    const upsellOriginParam = buildUpsellOriginParam(componentName, requestSource, 'Upselling Kit')
    const additionalParams = action
      ? `${upsellOriginParam ?? ''}${upsellOriginParam ? '&' : ''}action=${action}`
      : upsellOriginParam
    return getDetailPath(moduleSlug ?? '', additionalParams ?? '')
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_modal',
      cta: 'learn_more',
    })
    navigate(getDetailPathWithParams())
    onClose()
  }

  const handleBookMeeting = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_modal',
      cta: 'book_meeting',
    })
    navigate(getDetailPathWithParams('book_meeting'))
    onClose()
  }

  const handleProductDemo = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_modal',
      cta: 'product_demo',
    })
    navigate(getDetailPathWithParams('open_demo'))
    onClose()
  }

  const handleGetInfoByEmail = async () => {
    try {
      await createEmailRequest({
        moduleName: moduleRequested,
      })
      onRequest?.()
      app.setFlash({
        type: 'correct',
        message: i18n.t('growth.product_modal_extended.email_sent'),
      })
      onClose()
      track(trackingEvents.upsellingKit.sendEmail, {
        source: requestSource,
        module: moduleRequested,
        component: 'product_modal',
      })
    } catch {}
  }

  if (loading) {
    return (
      <Box
        width="full"
        height="full"
        justifyContent="center"
        alignItems="center"
        style={{
          background: 'rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.modals,
          position: 'fixed',
        }}
      >
        <Spinner />
      </Box>
    )
  }

  if (!isOpen) return null

  const modalContent = (
    <>
      <ScrollbarStyles />
      <Box
        width="full"
        height="full"
        justifyContent="center"
        alignItems="center"
        style={{
          background: 'rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.modals,
          position: 'fixed',
          top: '0px',
          left: '0px',
          right: '0px',
          bottom: '0px',
        }}
        onClick={handleOnClose}
      >
        <Box
          background="grey000White"
          borderRadius={{ all: 'abs016' }}
          position="relative"
          flexDirection="column"
          style={{
            height: '627px',
            width: '860px',
            cursor: 'default',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader
            moduleAvatar={moduleAvatar}
            moduleName={moduleName}
            moduleDescription={moduleDescription}
            onClose={handleOnClose}
          />
          {hasPlansContent ? (
            <BaseTabs
              tabs={[
                {
                  id: 'overview',
                  label: i18n.t('growth.product_modal_extended.overview'),
                  onClick: () => setActiveTabId('overview'),
                },
                {
                  id: 'plans',
                  label: i18n.t('growth.product_modal_extended.plans'),
                  onClick: () => setActiveTabId('plans'),
                },
              ]}
              activeTabId={activeTabId}
              setActiveTabId={setActiveTabId}
            />
          ) : null}
          <Box
            paddingTop="s0"
            alignItems="flexStart"
            style={{ flex: '1 1 0%', minHeight: '0', display: 'flex', flexDirection: 'column' }}
          >
            {(activeTabId === 'overview' || !hasPlansContent) && (
              <OverviewTab
                moduleCarouselVideo={moduleCarouselVideo}
                moduleCarouselImages={moduleCarouselImages}
                moduleSlug={moduleSlug}
                validFeatures={validFeatures}
                navatticImageUrl={navatticImageUrl ?? null}
                upsellModalBenefits={whatsIncludedBenefits}
                onLearnMore={handleLearnMore}
                onProductDemo={handleProductDemo}
              />
            )}
            {hasPlansContent && activeTabId === 'plans' && (
              <PlansTab
                billingAddonReference={billingAddonReference}
                moduleRequested={moduleRequested}
              />
            )}
          </Box>
          <ModalFooter
            onGetInfoByEmail={handleGetInfoByEmail}
            onLearnMore={handleLearnMore}
            onBookMeeting={handleBookMeeting}
            hasMeetingLink={hasMeetingLink}
          />
        </Box>
      </Box>
    </>
  )

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null
}

const ProductModalExtended = ({
  fallbackComponent,
  isOpen,
  onClose,
  openFromUpsellButton = true,
  children,
  ...upsellProps
}: ProductModalExtendedProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <ProductModalExtendedContent
        {...upsellProps}
        isOpen={isOpen}
        onClose={onClose}
        openFromUpsellButton={openFromUpsellButton}
      >
        {children}
      </ProductModalExtendedContent>
    </UpsellGuard>
  )
}

export { ProductModalExtended }
